import React, { useMemo, useState } from 'react';
import { Typography, TextField, Checkbox } from '@scale/scaleui';
import MultiSelect from 'corp/components/UI/Select';
import useMoveWorkerTeamCard from './useMoveWorkerTeamCard';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import pluralize from 'pluralize';
import {
  WorkerTeamAssignmentReason,
  WorkerTeamEventType,
} from '@scale/shared/types/events/WorkerTeamEvent';
import { Column } from '@scale/compass-shared/types';
import { WorkerTeam } from '@scale/shared/workerTeams/types';
import { twMerge } from 'tailwind-merge';
import { compassErrorClassName } from 'react-scale/corp/views/DataCompassCardsV2/utils';
import { Tabs } from '@scale/scaleui-radix';
import { useApiFetchGetRequest } from 'react-scale/hooks/useApiFetchGetRequest';

const REASON_TO_TEXT_MAP: Record<WorkerTeamAssignmentReason, string> = {
  [WorkerTeamAssignmentReason.ProjectClosure]: 'Project Closure',
  [WorkerTeamAssignmentReason.PriorityChange]: 'Change in Priorities',
  [WorkerTeamAssignmentReason.LimitedWork]: 'Limited Work on Current Project',
  [WorkerTeamAssignmentReason.SelfRequest]: 'Self Request',
  [WorkerTeamAssignmentReason.NewOrReactivated]: 'New or Reactivated',
  [WorkerTeamAssignmentReason.Promotion]: 'Promotion',
  [WorkerTeamAssignmentReason.Demotion]: 'Demotion',
  [WorkerTeamAssignmentReason.PoorQuality]: 'Poor Quality',
  [WorkerTeamAssignmentReason.Fraud]: 'Fraud',
  [WorkerTeamAssignmentReason.AllocationMistake]: 'Allocation Mistake',
  [WorkerTeamAssignmentReason.Generic]: 'Generic Reason',
  [WorkerTeamAssignmentReason.NoTaskerFacingReason]: 'No Tasker Facing Reason',
  [WorkerTeamAssignmentReason.Training]: 'Training',
  [WorkerTeamAssignmentReason.WorkerInactive]: 'Worker Inactive',
};

const ColumnWithWorkerIdsSelector = ({
  numWorkers,
  columns,
  selectedColumnIdWithWorkerIds,
  setSelectedColumnIdWithWorkerIds,
  isColumnSelectionInvalid,
}: {
  numWorkers: number | undefined;
  columns: Column[];
  selectedColumnIdWithWorkerIds: string;
  setSelectedColumnIdWithWorkerIds: (columnId: string) => void;
  isColumnSelectionInvalid: boolean;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <Typography.Subtitle3>Column with Worker IDs*</Typography.Subtitle3>
      {numWorkers && numWorkers > 0 && (
        <Typography.Caption>
          {`Selected column has ${numWorkers} ${pluralize('worker', numWorkers)} to be moved.`}
        </Typography.Caption>
      )}
      <MultiSelect
        required
        className={twMerge('rounded-lg', isColumnSelectionInvalid && compassErrorClassName)}
        label="Select column"
        multi={false}
        options={columns.map(column => ({
          value: column.id ?? '-',
          label: column.header ?? '-',
        }))}
        disabled={false}
        placeholder={'Select a column with worker ids...'}
        isSearchable={true}
        isClearable={true}
        value={selectedColumnIdWithWorkerIds}
        onChange={(e: any) => {
          setSelectedColumnIdWithWorkerIds(e === null ? null : e.value);
        }}
      />
    </div>
  );
};

const NewWorkerTeamIdInput = ({
  eventType,
  newWorkerTeamId,
  setNewWorkerTeamId,
  hasError,
}: {
  newWorkerTeamId: string | undefined;
  eventType: WorkerTeamEventType;
  setNewWorkerTeamId: (newWorkerTeamId: string) => void;
  hasError: boolean;
}) => {
  const title =
    eventType === WorkerTeamEventType.AddWorker
      ? 'New worker team ID*'
      : 'Worker team ID to remove*';
  return (
    <div className="flex flex-col gap-1">
      <Typography.Subtitle3>{title}</Typography.Subtitle3>
      <TextField
        required
        id="workerTeamId"
        placeholder="Enter a worker team ID..."
        value={newWorkerTeamId}
        onChange={e => setNewWorkerTeamId(e.target.value)}
        error={hasError && !newWorkerTeamId}
      />
    </div>
  );
};

const ReasonForMoveSelector = ({
  reason,
  setReason,
  hasError,
  additionalReasonContext,
  setAdditionalReasonContext,
}: {
  reason: string | undefined;
  setReason: (reason: string | undefined) => void;
  hasError: boolean;
  additionalReasonContext: string | undefined;
  setAdditionalReasonContext: (additionalReasonContext: string | undefined) => void;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <Typography.Subtitle3>Reason for move</Typography.Subtitle3>
      <MultiSelect
        className={twMerge('rounded-lg', hasError && !reason && compassErrorClassName)}
        multi={false}
        options={Object.values(WorkerTeamAssignmentReason).map(reason => {
          return {
            value: reason,
            label: REASON_TO_TEXT_MAP[reason as WorkerTeamAssignmentReason],
          };
        })}
        placeholder={'Select a reason...'}
        value={reason}
        onChange={(e: any) => setReason(e?.value)}
      />
      <Typography.Paragraph2>Additional context (optional)</Typography.Paragraph2>
      <TextField
        placeholder="Additional context for the move..."
        value={additionalReasonContext ?? ''}
        onChange={e => setAdditionalReasonContext(e.target.value)}
      />
    </div>
  );
};

const CapacityReqIdInput = ({
  capacityReqId,
  setCapacityReqId,
}: {
  capacityReqId: string | undefined;
  setCapacityReqId: (capacityReqId: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <Typography.Subtitle3>Capacity request ID</Typography.Subtitle3>
      <TextField
        id="capacityReqId"
        placeholder="Enter a capacity request ID..."
        value={capacityReqId}
        onChange={e => setCapacityReqId(e.target.value)}
        helperText="The capacity request ID associated with worker movement (tag capacity_request_(ID value) will be created automatically)"
      />
    </div>
  );
};

const RemoveDestinationWorkerTeamSelector = ({
  destinationWorkerTeamId,
  setDestinationWorkerTeamId,
  teamId,
}: {
  destinationWorkerTeamId: string;
  setDestinationWorkerTeamId: (destinationWorkerTeamId: string) => void;
  teamId: string | undefined;
}) => {
  const freeAgentTeams = useApiFetchGetRequest<WorkerTeam[]>(
    `corp-api/worker_teams/team/${teamId}/freeAgentTeams`,
    { shouldShortCircuit: !teamId },
  );

  const freeAgentTeamsOptions = useMemo(() => {
    return (freeAgentTeams.response || []).map(team => ({
      value: team.id,
      label: team.name,
    }));
  }, [freeAgentTeams.response]);

  return (
    <div className="flex flex-col gap-1">
      <Typography.Subtitle3>
        Destination Worker Team (Must be a designated free agent team)
      </Typography.Subtitle3>
      <MultiSelect
        className="rounded-lg"
        multi={false}
        options={freeAgentTeamsOptions}
        placeholder="Select a free agent team..."
        value={destinationWorkerTeamId}
        onChange={(e: any) => {
          setDestinationWorkerTeamId(e === null ? '' : e.value);
        }}
      />
    </div>
  );
};

const MoveWorkerTeamCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const [currentTab, setCurrentTab] = useState<WorkerTeamEventType>(WorkerTeamEventType.AddWorker);

  const {
    newWorkerTeamId,
    setNewWorkerTeamId,
    selectedColumnIdWithWorkerIds,
    setSelectedColumnIdWithWorkerIds,
    reason,
    setReason,
    autopermission,
    setAutopermission,
    numWorkers,
    columns,
    hasError,
    dryRun,
    setDryRun,
    rejectAttemptsForMovedAttempters,
    setRejectAttemptsForMovedAttempters,
    excludeViolationsWTContributors,
    setExcludeViolationsWTContributors,
    capacityReqId,
    setCapacityReqId,
    removeGenAiPermissions,
    setRemoveGenAiPermissions,
    removalDestinationTeam,
    setRemovalDestinationTeam,
    setEventType,
    additionalReasonContext,
    setAdditionalReasonContext,
  } = useMoveWorkerTeamCard(index);

  const isColumnSelectionInvalid = hasError && !selectedColumnIdWithWorkerIds;

  const onTabChange = (tab: string) => {
    const eventType = tab as WorkerTeamEventType;
    setCurrentTab(eventType);
    setEventType(eventType);
  };

  return (
    <Tabs.Root value={currentTab} onValueChange={onTabChange} className="space-y-4">
      <Tabs.List>
        <Tabs.Trigger value={WorkerTeamEventType.AddWorker}>Add Workers</Tabs.Trigger>
        <Tabs.Trigger value={WorkerTeamEventType.RemoveWorker}>Remove Workers</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value={WorkerTeamEventType.AddWorker}>
        <div className="flex flex-col gap-4 w-100">
          <ColumnWithWorkerIdsSelector
            numWorkers={numWorkers}
            columns={columns || []}
            selectedColumnIdWithWorkerIds={selectedColumnIdWithWorkerIds || ''}
            setSelectedColumnIdWithWorkerIds={setSelectedColumnIdWithWorkerIds}
            isColumnSelectionInvalid={isColumnSelectionInvalid}
          />
          <NewWorkerTeamIdInput
            newWorkerTeamId={newWorkerTeamId}
            eventType={WorkerTeamEventType.AddWorker}
            setNewWorkerTeamId={setNewWorkerTeamId}
            hasError={hasError}
          />
          <ReasonForMoveSelector
            reason={reason}
            setReason={setReason}
            hasError={hasError}
            additionalReasonContext={additionalReasonContext}
            setAdditionalReasonContext={setAdditionalReasonContext}
          />
          <CapacityReqIdInput capacityReqId={capacityReqId} setCapacityReqId={setCapacityReqId} />
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Grant minimum permissions for destination team"
              checked={autopermission}
              onChange={e => setAutopermission(e.target.checked)}
            />
            <Typography.Caption className="text-neutral-600">
              If enabled, workers will be granted attempt permissions to all assignments of the
              teams they are added to
            </Typography.Caption>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Reject attempts for moved attempters"
              checked={rejectAttemptsForMovedAttempters}
              onChange={e => setRejectAttemptsForMovedAttempters(e.target.checked)}
            />
            <Typography.Caption className="text-neutral-600">
              Typically used if destination team is disabled
            </Typography.Caption>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Exclude violations WT contributors"
              checked={excludeViolationsWTContributors}
              onChange={e => setExcludeViolationsWTContributors(e.target.checked)}
            />
            <Typography.Caption className="text-neutral-600">
              Do not move contributors currently in violations-related worker teams
            </Typography.Caption>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Dry run"
              checked={dryRun}
              onChange={e => setDryRun(e.target.checked)}
            />
            <Typography.Caption className="text-neutral-600">
              No changes will be made after the run is submitted
            </Typography.Caption>
          </div>
        </div>
      </Tabs.Content>
      <Tabs.Content value={WorkerTeamEventType.RemoveWorker}>
        <div className="flex flex-col gap-4">
          <ColumnWithWorkerIdsSelector
            numWorkers={numWorkers}
            columns={columns || []}
            selectedColumnIdWithWorkerIds={selectedColumnIdWithWorkerIds || ''}
            setSelectedColumnIdWithWorkerIds={setSelectedColumnIdWithWorkerIds}
            isColumnSelectionInvalid={isColumnSelectionInvalid}
          />
          <NewWorkerTeamIdInput
            newWorkerTeamId={newWorkerTeamId}
            eventType={WorkerTeamEventType.RemoveWorker}
            setNewWorkerTeamId={setNewWorkerTeamId}
            hasError={hasError}
          />
          <RemoveDestinationWorkerTeamSelector
            destinationWorkerTeamId={removalDestinationTeam || ''}
            setDestinationWorkerTeamId={setRemovalDestinationTeam}
            teamId={newWorkerTeamId}
          />
          <ReasonForMoveSelector
            reason={reason}
            setReason={setReason}
            hasError={hasError}
            additionalReasonContext={additionalReasonContext}
            setAdditionalReasonContext={setAdditionalReasonContext}
          />
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Remove all Gen AI permissions from removed taskers"
              checked={removeGenAiPermissions}
              onChange={e => setRemoveGenAiPermissions(e.target.checked)}
            />
            <Typography.Caption className="text-neutral-600">
              If enabled, all Gen AI permissions will be removed from designated taskers.
            </Typography.Caption>
          </div>
          <div className="flex flex-col gap-1">
            <Checkbox
              label="Dry run"
              checked={dryRun}
              onChange={e => setDryRun(e.target.checked)}
            />
            <Typography.Caption className="text-neutral-600">
              No changes will be made after the run is submitted
            </Typography.Caption>
          </div>
        </div>
      </Tabs.Content>
    </Tabs.Root>
  );
};

export default MoveWorkerTeamCardComponent;
